import type { MailboxProvider } from '@immo/api-contract';
import { Button, ProviderLogo } from '@immo/ui-kit';
import { MailIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';

const PROVIDER_KEY: Record<MailboxProvider, 'google' | 'microsoft' | 'imap'> = {
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
  IMAP: 'imap',
};

type ProviderChoiceProps = {
  readonly offered: ReadonlyArray<MailboxProvider>;
  readonly selected: MailboxProvider;
  readonly onSelect: (provider: MailboxProvider) => void;
  // The radiogroup's accessible name.
  readonly label: string;
};

export function ProviderChoice({ offered, selected, onSelect, label }: ProviderChoiceProps) {
  const t = useTranslations('pages.workspace.mailboxConnectDialog');

  return (
    <div role="radiogroup" aria-label={label} className="space-y-2">
      {offered.map((provider) => {
        const key = PROVIDER_KEY[provider];
        const active = selected === provider;

        return (
          // The provider cards are kit `secondary` buttons (IMM-741.5) — the
          // variant already draws the bordered surface tile the ⑧ artboard
          // asks for. `h-auto` + `flex-col items-start` + `whitespace-normal`
          // pin back the kit's single-line, centred, nowrap default; the
          // selected card overrides the border and fill only.
          <Button
            key={provider}
            type="button"
            role="radio"
            variant="secondary"
            size={null}
            aria-checked={active}
            onClick={() => onSelect(provider)}
            className={`h-auto w-full justify-start whitespace-normal px-4 py-3 text-left font-normal ${
              active ? 'border-interactive bg-interactive/5 hover:bg-interactive/5' : 'bg-transparent'
            }`}
          >
            <span className="flex items-start gap-3">
              {/* IMAP has no brand: a neutral mail icon, never a logo (DESIGN.md, IMM-1173). */}
              {provider === 'IMAP' ? (
                <MailIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
              ) : (
                <ProviderLogo provider={provider} size={20} className="mt-0.5" />
              )}
              <span className="flex flex-col items-start gap-0.5">
                <span className="text-base-sm font-medium text-text-primary">{t(`provider-${key}-label`)}</span>
                <span className="text-xs text-text-tertiary">{t(`provider-${key}-desc`)}</span>
              </span>
            </span>
          </Button>
        );
      })}
    </div>
  );
}
